/* ═══════════════════════════════════════════════════════════════
   People — small composed pieces shared across the section: a
   loading skeleton and a visible "data is stale" banner with a real
   retry button. Ported verbatim (behavior-identical) from
   pages/team/kit.tsx for the People rebuild — self-contained so
   this section doesn't depend on the old team/ surfaces that get
   deleted after verification (docs/PEOPLE_REBUILD_PLAN.md §1).
   ═══════════════════════════════════════════════════════════════ */

import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';

/** Placeholder rows matching the shape of a DataTable while the first
 *  load is in flight, so the tab never flashes an empty/blank frame. */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="overflow-hidden rounded-md border border-border"
    >
      <div className="flex items-center gap-4 border-b border-border bg-surface-alt px-4 py-2.5">
        <div className="h-3 w-24 animate-pulse rounded bg-border" />
        <div className="h-3 w-32 animate-pulse rounded bg-border" />
        <div className="ml-auto h-3 w-16 animate-pulse rounded bg-border" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="h-3 w-8 shrink-0 animate-pulse rounded-full bg-surface-alt" />
          <div className="h-3 w-36 animate-pulse rounded bg-surface-alt" />
          <div className="h-3 w-44 animate-pulse rounded bg-surface-alt" />
          <div className="ml-auto h-3 w-14 animate-pulse rounded bg-surface-alt" />
        </div>
      ))}
    </div>
  );
}

/** Shown when a background refresh failed after the automatic retry — the
 *  page keeps showing the last-known-good cached data (never blank), but
 *  the user is told it may be stale and given a real retry action instead
 *  of a silently-console-logged failure. */
export function StaleDataBanner({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-md border px-4 py-2.5 text-sm text-text"
      style={{
        borderColor: 'color-mix(in srgb, var(--color-warning) 35%, transparent)',
        background: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
      }}
    >
      <Icon icon={AlertTriangle} em={1.4545} className="shrink-0 text-warning" aria-hidden="true" />
      <span className="flex-1">
        Some people data couldn&rsquo;t be refreshed — you may be looking at stale information.
      </span>
      <Button variant="outline" size="sm" onClick={onRetry} className="gap-1.5">
        <Icon icon={RefreshCw} em={1.4} aria-hidden="true" />
        Retry
      </Button>
    </div>
  );
}
